import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { 
  MonitoredURL, 
  URLMetrics, 
  URLAlert, 
  URLSummary, 
  URLCheckResult,
  URLWebSocketMessage,
  URLStatus
} from '../../types/urls';
import { services } from '../../services/api';

interface URLState {
  selectedURL: MonitoredURL | null;
  urls: MonitoredURL[];
  metrics: Record<string, URLMetrics>;
  alerts: URLAlert[];
  realtimeMetrics: Record<string, any>;
  checkHistory: Record<string, URLCheckResult[]>;
  summary: URLSummary | null;
  isLoading: boolean;
  isCheckingUrl: boolean;
  error: string | null;
  filters: {
    status: URLStatus | 'all';
    search: string;
    tags: string[];
  };
  pagination: {
    page: number;
    pageSize: number;
    total: number;
  };
  websocketStatus: {
    alertsConnected: boolean;
    metricsConnected: Record<string, boolean>;
  };
}

const initialState: URLState = {
  selectedURL: null,
  urls: [],
  metrics: {},
  alerts: [],
  realtimeMetrics: {},
  checkHistory: {},
  summary: null,
  isLoading: false,
  isCheckingUrl: false,
  error: null,
  filters: {
    status: 'all',
    search: '',
    tags: [],
  },
  pagination: {
    page: 0,
    pageSize: 25,
    total: 0,
  },
  websocketStatus: {
    alertsConnected: false,
    metricsConnected: {},
  },
};

// Async thunks
export const fetchURLs = createAsyncThunk(
  'urls/fetchURLs',
  async (params: {
    page?: number;
    limit?: number;
    search?: string;
    status?: URLStatus | 'all';
  }) => {
    const response = await services.urls.getMonitors({
      page: (params.page || 0) + 1,
      limit: params.limit || 25,
      ...(params.search && { search: params.search }),
      ...(params.status && params.status !== 'all' && { status: params.status }),
    });
    return response.data;
  }
);

export const fetchURLMetrics = createAsyncThunk(
  'urls/fetchURLMetrics',
  async (urlId: string) => {
    const response = await services.urls.getMonitor(urlId);
    return { urlId, metrics: response.data.metrics || response.data };
  }
);

export const checkURL = createAsyncThunk(
  'urls/checkURL',
  async (urlId: string) => {
    const response = await services.urls.checkUrl(urlId);
    return response.data;
  }
);

export const fetchCheckHistory = createAsyncThunk(
  'urls/fetchCheckHistory',
  async ({ urlId, params }: { urlId: string; params?: { limit?: number; page?: number } }) => {
    const response = await services.urls.getCheckHistory(urlId, params);
    return { urlId, history: response.data };
  }
);

const urlSlice = createSlice({
  name: 'urls',
  initialState,
  reducers: {
    setSelectedURL: (state, action: PayloadAction<MonitoredURL | null>) => {
      state.selectedURL = action.payload;
    },
    setURLs: (state, action: PayloadAction<MonitoredURL[]>) => {
      state.urls = action.payload;
    },
    updateURL: (state, action: PayloadAction<MonitoredURL>) => {
      const index = state.urls.findIndex(url => url.id === action.payload.id);
      if (index !== -1) {
        state.urls[index] = action.payload;
      }
      if (state.selectedURL?.id === action.payload.id) {
        state.selectedURL = action.payload;
      }
    },
    removeURL: (state, action: PayloadAction<string>) => {
      state.urls = state.urls.filter(url => url.id !== action.payload);
      if (state.selectedURL?.id === action.payload) {
        state.selectedURL = null;
      }
      delete state.metrics[action.payload];
      delete state.realtimeMetrics[action.payload];
      delete state.checkHistory[action.payload];
      delete state.websocketStatus.metricsConnected[action.payload];
    },
    updateURLMetrics: (state, action: PayloadAction<{ urlId: string; metrics: URLMetrics }>) => {
      state.metrics[action.payload.urlId] = action.payload.metrics;
    },
    updateRealtimeMetrics: (state, action: PayloadAction<{ urlId: string; metrics: any }>) => {
      state.realtimeMetrics[action.payload.urlId] = action.payload.metrics;
    },
    addAlert: (state, action: PayloadAction<URLAlert>) => {
      state.alerts = [action.payload, ...state.alerts.slice(0, 49)]; // Keep last 50 alerts
    },
    updateAlert: (state, action: PayloadAction<URLAlert>) => {
      const index = state.alerts.findIndex(alert => alert.id === action.payload.id);
      if (index !== -1) {
        state.alerts[index] = action.payload;
      }
    },
    removeAlert: (state, action: PayloadAction<string>) => {
      state.alerts = state.alerts.filter(alert => alert.id !== action.payload);
    },
    clearAlerts: (state) => {
      state.alerts = [];
    },
    clearAlertsForURL: (state, action: PayloadAction<string>) => {
      state.alerts = state.alerts.filter(alert => alert.url_id !== action.payload);
    },
    addCheckHistoryEntry: (state, action: PayloadAction<{ urlId: string; check: URLCheckResult }>) => {
      const { urlId, check } = action.payload;
      if (!state.checkHistory[urlId]) {
        state.checkHistory[urlId] = [];
      }
      state.checkHistory[urlId] = [check, ...state.checkHistory[urlId].slice(0, 99)]; // Keep last 100 entries
    },
    setCheckHistory: (state, action: PayloadAction<{ urlId: string; history: URLCheckResult[] }>) => {
      state.checkHistory[action.payload.urlId] = action.payload.history;
    },
    setSummary: (state, action: PayloadAction<URLSummary>) => {
      state.summary = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setCheckingUrl: (state, action: PayloadAction<boolean>) => {
      state.isCheckingUrl = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setFilters: (state, action: PayloadAction<Partial<typeof initialState.filters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
    },
    setPagination: (state, action: PayloadAction<Partial<typeof initialState.pagination>>) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    setWebSocketStatus: (state, action: PayloadAction<Partial<typeof initialState.websocketStatus>>) => {
      state.websocketStatus = { ...state.websocketStatus, ...action.payload };
    },
    setMetricsWebSocketConnected: (state, action: PayloadAction<{ urlId: string; connected: boolean }>) => {
      state.websocketStatus.metricsConnected[action.payload.urlId] = action.payload.connected;
    },
    setAlertsWebSocketConnected: (state, action: PayloadAction<boolean>) => {
      state.websocketStatus.alertsConnected = action.payload;
    },
    // Handle WebSocket messages
    handleWebSocketMessage: (state, action: PayloadAction<URLWebSocketMessage>) => {
      const message = action.payload;
      
      switch (message.type) {
        case 'metrics':
          if (message.data) {
            state.realtimeMetrics[message.url_id] = message.data;
          }
          break;
        case 'alert':
          if (message.data) {
            state.alerts = [message.data, ...state.alerts.slice(0, 49)];
          }
          break;
        case 'status_change':
          if (message.data) {
            const urlIndex = state.urls.findIndex(url => url.id === message.url_id);
            if (urlIndex !== -1 && state.metrics[message.url_id]) {
              state.metrics[message.url_id].current_status = message.data.status;
            }
          }
          break;
        case 'check_result':
          if (message.data) {
            const { urlId, check } = message.data;
            if (!state.checkHistory[urlId]) {
              state.checkHistory[urlId] = [];
            }
            state.checkHistory[urlId] = [check, ...state.checkHistory[urlId].slice(0, 99)];
          }
          break;
      }
    },
    // Bulk operations
    updateMultipleURLs: (state, action: PayloadAction<MonitoredURL[]>) => {
      const updatedUrls = action.payload;
      updatedUrls.forEach(updatedUrl => {
        const index = state.urls.findIndex(url => url.id === updatedUrl.id);
        if (index !== -1) {
          state.urls[index] = updatedUrl;
        }
      });
    },
    updateMultipleMetrics: (state, action: PayloadAction<Record<string, URLMetrics>>) => {
      Object.entries(action.payload).forEach(([urlId, metrics]) => {
        state.metrics[urlId] = metrics;
      });
    },
  },
  extraReducers: (builder) => {
    // fetchURLs
    builder
      .addCase(fetchURLs.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchURLs.fulfilled, (state, action) => {
        state.isLoading = false;
        state.urls = action.payload.urls || [];
        state.pagination.total = action.payload.total || 0;
        if (action.payload.summary) {
          state.summary = action.payload.summary;
        }
      })
      .addCase(fetchURLs.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch URLs';
      });

    // fetchURLMetrics
    builder
      .addCase(fetchURLMetrics.fulfilled, (state, action) => {
        state.metrics[action.payload.urlId] = action.payload.metrics;
      })
      .addCase(fetchURLMetrics.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to fetch URL metrics';
      });

    // checkURL
    builder
      .addCase(checkURL.pending, (state) => {
        state.isCheckingUrl = true;
      })
      .addCase(checkURL.fulfilled, (state) => {
        state.isCheckingUrl = false;
      })
      .addCase(checkURL.rejected, (state, action) => {
        state.isCheckingUrl = false;
        state.error = action.error.message || 'Failed to check URL';
      });

    // fetchCheckHistory
    builder
      .addCase(fetchCheckHistory.fulfilled, (state, action) => {
        state.checkHistory[action.payload.urlId] = action.payload.history.checks || [];
      })
      .addCase(fetchCheckHistory.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to fetch check history';
      });
  },
});

export const {
  setSelectedURL,
  setURLs,
  updateURL,
  removeURL,
  updateURLMetrics,
  updateRealtimeMetrics,
  addAlert,
  updateAlert,
  removeAlert,
  clearAlerts,
  clearAlertsForURL,
  addCheckHistoryEntry,
  setCheckHistory,
  setSummary,
  setLoading,
  setCheckingUrl,
  setError,
  setFilters,
  resetFilters,
  setPagination,
  setWebSocketStatus,
  setMetricsWebSocketConnected,
  setAlertsWebSocketConnected,
  handleWebSocketMessage,
  updateMultipleURLs,
  updateMultipleMetrics,
} = urlSlice.actions;

export default urlSlice.reducer;